import { Badge } from "@/components/ui/badge";
import type { ProgramNewsItem } from "@/services/adminProgramNews";
import { formatConfidence } from "./formatters";
import { classBadgeVariant } from "./deliveryClass";
import { GLOSSARY, type GlossaryKey } from "./glossary";
import ReasonLine from "./ReasonLine";
import Term from "./Term";

interface ArticleDetailProps {
  item: ProgramNewsItem;
}

/** The status-specific column: class/type/confidence, suppression rationale, or the error. */
const ArticleDetail = ({ item }: ArticleDetailProps) => {
  const classification = item.rawMetadata?.classification ?? null;

  if (item.processingStatus === "classified") {
    if (!classification) return <ReasonLine item={item} full />;
    return (
      <div className="space-y-1">
        <div className="flex flex-wrap items-center gap-1 text-xs">
          {classification.deliveryClass ? (
            <Badge variant={classBadgeVariant(classification.deliveryClass)}>
              <Term term={classification.deliveryClass}>{classification.deliveryClass}</Term>
            </Badge>
          ) : null}
          {classification.eventType ? (
            <span>
              {classification.eventType in GLOSSARY ? (
                <Term term={classification.eventType as GlossaryKey}>
                  {classification.eventType}
                </Term>
              ) : (
                classification.eventType
              )}
            </span>
          ) : null}
          {typeof classification.confidence === "number" ? (
            <span className="text-muted-foreground">
              {formatConfidence(classification.confidence)}
            </span>
          ) : null}
          {classification.escalated ? (
            <Badge variant="outline" title="Escalated to the stronger model">
              escalated
            </Badge>
          ) : null}
        </div>
        <ReasonLine item={item} full />
      </div>
    );
  }

  if (item.processingStatus === "suppressed") {
    return (
      <div className="space-y-1">
        <ReasonLine item={item} full />
        {item.suppressedRule ? (
          <div className="text-xs text-muted-foreground">Rule: {item.suppressedRule}</div>
        ) : null}
      </div>
    );
  }

  if (item.processingStatus === "failed") {
    return (
      <span className="text-xs text-destructive break-words">
        {classification?.error || "Classification failed"}
      </span>
    );
  }

  return <span className="text-muted-foreground">—</span>;
};

export default ArticleDetail;
